import { FC, ReactNode, createContext, useCallback, useContext } from 'react';

import { traceContextManager } from './TraceContextManager';

interface TraceContextValue {
  withTrace: <T>(
    spanName: string,
    fn: () => T | Promise<T>,
    attributes?: Record<string, string | number | boolean>
  ) => Promise<T>;
}

const TraceContext = createContext<TraceContextValue | undefined>(undefined);

interface TraceProviderProps {
  children: ReactNode;
}

/**
 * TraceProvider syncs with the global TraceContextManager
 * This ensures React components and non-React code (e.g., Apollo Links)
 * share the same trace context
 */
export const TraceProvider: FC<TraceProviderProps> = ({ children }) => {
  const withTrace = useCallback(
    <T,>(
      spanName: string,
      fn: () => T | Promise<T>,
      attributes?: Record<string, string | number | boolean>
    ): Promise<T> => traceContextManager.withTrace(spanName, fn, attributes),
    []
  );

  const value: TraceContextValue = {
    withTrace,
  };

  return <TraceContext value={value}>{children}</TraceContext>;
};

/**
 * Hook to access trace context
 * Must be used within a TraceProvider
 */
export const useTrace = (): TraceContextValue => {
  const context = useContext(TraceContext);
  if (!context) {
    throw new Error('useTrace must be used within a TraceProvider');
  }
  return context;
};
